import { useState, useEffect } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, GIVESENDGO_URL } from "../lib/api";

const TIERS = [
    {
        amount: 25,
        name: "The Page",
        tag: "Paying attention.",
        perks: [
            "Name on Supporters page",
            "Ongoing campaign updates",
            "Early access to the teaser trailer",
        ],
    },
    {
        amount: 50,
        name: "The Chief of Staff",
        tag: "Stewarding power and conscience.",
        perks: [
            "Everything above",
            "Private behind-the-scenes update",
            "Name in end credits — “Special Thanks”",
        ],
    },
    {
        amount: 100,
        name: "The Whistleblower",
        tag: "Choosing courage over silence.",
        perks: [
            "Everything above",
            "Private livestream Q&A in post-production",
            "Early online access to the completed film",
            "Name in credits — “Associate Supporters”",
        ],
    },
    {
        amount: "custom",
        name: "The Senator",
        tag: "Name your own stake.",
        featured: true,
        perks: [
            "Everything above",
            "Give any amount on GiveSendGo",
            "$250+: private virtual screening + discussion",
            "$250+: personal thank-you message",
            "$250+: name in credits — “Campaign Patrons”",
        ],
    },
];

const ROLES = [
    "Lead Senator (male, 35-55)",
    "Investigative Journalist (any gender, 28-45)",
    "Senior Advisor / Whistleblower (40-65)",
    "Activist / Protester (18-35)",
    "Federal Agent (30-55)",
    "Background / Crowd Extra",
    "Crew / Production Volunteer",
    "Other (specify in notes)",
];

const initialForm = {
    full_name: "",
    email: "",
    phone: "",
    age: "",
    location: "",
    role_interest: "",
    acting_experience: "",
    headshot_link: "",
    availability: "",
    additional_notes: "",
};

export default function GetInvolved() {
    const [stats, setStats] = useState({ goal: 200000, raised: 18507, backers: 72 });
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        api.get("/fundraising/stats")
            .then((r) => setStats(r.data))
            .catch(() => {});
    }, []);

    const pct = Math.min(100, (stats.raised / stats.goal) * 100);

    const handleChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await api.post("/casting", {
                ...form,
                age: parseInt(form.age, 10),
            });
            setSubmitted(true);
            setForm(initialForm);
            toast.success("Application received. We'll be in touch.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="get-involved-page" className="pt-32 pb-20">
            {/* Header */}
            <section className="max-w-7xl mx-auto px-6 sm:px-8 mb-16">
                <p className="font-mono text-base uppercase tracking-[0.3em] text-neutral-500 mb-6">
                    File 03 / Take Your Position
                </p>
                <h1
                    data-testid="get-involved-title"
                    className="font-display text-6xl sm:text-8xl lg:text-9xl uppercase leading-[0.95] tracking-tighter text-neutral-900 text-balance"
                >
                    Get
                    <br />
                    <span className="italic text-blood font-light">Involved.</span>
                </h1>
                <p className="font-mono text-xl sm:text-2xl text-neutral-600 mt-8 max-w-3xl leading-relaxed">
                    Two ways to help bring State of Deception to the screen — fund the film,
                    or step in front of the camera. Non-paid roles available.
                </p>
            </section>

            {/* Fundraising */}
            <section
                id="fund"
                data-testid="fund-section"
                className="max-w-7xl mx-auto px-6 sm:px-8 mb-32"
            >
                <div className="border border-neutral-300 bg-ink2 p-8 sm:p-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-10">
                        <div className="md:col-span-7">
                            <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-4">
                                / Campaign Status
                            </p>
                            <h2 className="font-display text-6xl sm:text-7xl uppercase tracking-tight text-neutral-900 text-balance">
                                Fund the Film
                            </h2>
                            <p className="font-mono text-lg text-neutral-600 mt-4 leading-relaxed">
                                Donations process securely on GiveSendGo. 100% of proceeds support
                                production, post-production, and distribution.
                            </p>
                        </div>
                        <div className="md:col-span-5 grid grid-cols-3 gap-4">
                            <Stat label="Raised" value={`$${stats.raised.toLocaleString()}`} />
                            <Stat label="Goal" value={`$${(stats.goal / 1000).toFixed(0)}k`} />
                            <Stat label="Backers" value={stats.backers} />
                        </div>
                    </div>

                    {/* progress */}
                    <div data-testid="progress-wrapper" className="mb-2 flex justify-between font-mono text-base uppercase tracking-widest text-neutral-500">
                        <span>{pct.toFixed(1)}% Funded</span>
                        <span>${stats.raised.toLocaleString()} / ${stats.goal.toLocaleString()}</span>
                    </div>
                    <div className="relative w-full h-2 bg-neutral-300 overflow-hidden">
                        <div
                            data-testid="fund-progress-bar"
                            className="h-full bg-blood transition-all duration-1000"
                            style={{ width: `${pct}%` }}
                        />
                    </div>

                    <a
                        href={GIVESENDGO_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="primary-donate-btn"
                        className="mt-10 inline-flex items-center justify-center gap-3 bg-blood text-white font-accent uppercase tracking-widest text-lg py-4 px-10 hover:bg-bloodHover transition-colors border border-blood"
                    >
                        Donate via GiveSendGo <ArrowRight size={16} />
                    </a>
                </div>

                {/* Tiers */}
                <h3 className="font-display text-5xl sm:text-6xl uppercase text-neutral-900 tracking-tight mt-20 mb-2">
                    How to Participate
                </h3>
                <p className="font-mono text-lg text-neutral-600 mb-10 max-w-2xl">
                    This film is about systems of power and personal responsibility. Choose where
                    you stand inside that system.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-300">
                    {TIERS.map((t) => (
                        <div
                            key={t.amount}
                            data-testid={`tier-${t.amount}`}
                            className={`p-8 flex flex-col ${
                                t.featured
                                    ? "bg-blood/10 border-2 border-blood"
                                    : "bg-ink hover:bg-ink3 transition-colors"
                            }`}
                        >
                            {t.featured && (
                                <span className="self-start mb-4 font-accent uppercase tracking-[0.25em] text-sm text-blood border border-blood px-2 py-1">
                                    Most Popular
                                </span>
                            )}
                            <p className="font-display text-7xl text-neutral-900">
                                {t.amount === "custom" ? (
                                    <>Custom</>
                                ) : (
                                    <>${t.amount}</>
                                )}
                            </p>
                            <p className="font-accent uppercase tracking-[0.25em] text-lg text-blood mt-3">
                                {t.name}
                            </p>
                            <p className="font-mono text-base text-neutral-500 italic mt-2">
                                {t.tag}
                            </p>
                            <ul className="mt-6 space-y-3 flex-1">
                                {t.perks.map((p) => (
                                    <li
                                        key={p}
                                        className="font-mono text-lg text-neutral-700 flex gap-3 items-start"
                                    >
                                        <Check size={14} className="text-blood mt-1 shrink-0" />
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                            <a
                                href={GIVESENDGO_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`tier-cta-${t.amount}`}
                                className={`mt-8 inline-flex items-center justify-center gap-2 font-accent uppercase tracking-widest text-base py-3 px-6 transition-colors border ${
                                    t.featured
                                        ? "bg-blood text-white border-blood hover:bg-bloodHover"
                                        : "bg-transparent text-neutral-900 border-neutral-400 hover:border-blood hover:text-blood"
                                }`}
                            >
                                {t.amount === "custom" ? (
                                    <>Choose an Amount <ArrowRight size={14} /></>
                                ) : (
                                    <>Pledge ${t.amount} <ArrowRight size={14} /></>
                                )}
                            </a>
                        </div>
                    ))}
                </div>
            </section>

            {/* Casting */}
            <section
                id="casting"
                data-testid="casting-section"
                className="max-w-7xl mx-auto px-6 sm:px-8"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-4">
                            / Casting Call — Open
                        </p>
                        <h2 className="font-display text-6xl sm:text-7xl uppercase text-neutral-900 tracking-tight leading-[1.05] text-balance">
                            Step in front of the camera.
                        </h2>
                        <p className="font-mono text-lg text-neutral-600 mt-6 leading-relaxed">
                            Non-paid roles available across leads, supporting, and background.
                            We&apos;re also looking for production volunteers. Submit your details and
                            we&apos;ll reach out for an audition or interview.
                        </p>
                        <div className="mt-8 border-l-2 border-blood pl-6 font-mono text-base uppercase tracking-[0.25em] text-neutral-500 space-y-2">
                            <p>● Roles: Lead, supporting, background, crew</p>
                            <p>● Compensation: Non-paid (credit + expenses)</p>
                            <p>● Locations: Real-world, contained</p>
                            <p>● Status: Accepting applications</p>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        {submitted ? (
                            <div
                                data-testid="casting-success"
                                className="border border-blood bg-blood/5 p-12 text-center"
                            >
                                <Check size={48} className="text-blood mx-auto mb-6" />
                                <h3 className="font-display text-5xl text-neutral-900 uppercase">
                                    Application Received
                                </h3>
                                <p className="font-mono text-lg text-neutral-600 mt-4 max-w-md mx-auto leading-relaxed">
                                    Thank you for applying. A casting director will reach out to
                                    schedule an audition or interview.
                                </p>
                                <button
                                    data-testid="casting-submit-another"
                                    onClick={() => setSubmitted(false)}
                                    className="mt-8 font-accent uppercase tracking-widest text-base text-blood hover:text-bloodHover"
                                >
                                    Submit another →
                                </button>
                            </div>
                        ) : (
                            <form
                                data-testid="casting-form"
                                onSubmit={handleSubmit}
                                className="border border-neutral-300 bg-ink2 p-8 sm:p-10 space-y-6"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <Field
                                        label="Full Name"
                                        name="full_name"
                                        value={form.full_name}
                                        onChange={handleChange}
                                        required
                                        testId="casting-input-name"
                                    />
                                    <Field
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        required
                                        testId="casting-input-email"
                                    />
                                    <Field
                                        label="Phone"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        required
                                        testId="casting-input-phone"
                                    />
                                    <Field
                                        label="Age"
                                        name="age"
                                        type="number"
                                        min="13"
                                        max="100"
                                        value={form.age}
                                        onChange={handleChange}
                                        required
                                        testId="casting-input-age"
                                    />
                                    <Field
                                        label="Location (City, State)"
                                        name="location"
                                        value={form.location}
                                        onChange={handleChange}
                                        required
                                        testId="casting-input-location"
                                    />
                                    <SelectField
                                        label="Role of Interest"
                                        name="role_interest"
                                        value={form.role_interest}
                                        onChange={handleChange}
                                        required
                                        options={ROLES}
                                        testId="casting-input-role"
                                    />
                                </div>

                                <TextAreaField
                                    label="Acting Experience"
                                    name="acting_experience"
                                    value={form.acting_experience}
                                    onChange={handleChange}
                                    placeholder="Theater, film, TV, student productions, training, etc. (Or 'none' — we welcome new talent.)"
                                    rows={3}
                                    required
                                    testId="casting-input-experience"
                                />

                                <Field
                                    label="Headshot / Reel Link (optional)"
                                    name="headshot_link"
                                    value={form.headshot_link}
                                    onChange={handleChange}
                                    placeholder="https://"
                                    type="url"
                                    testId="casting-input-headshot"
                                />

                                <Field
                                    label="Availability"
                                    name="availability"
                                    value={form.availability}
                                    onChange={handleChange}
                                    placeholder="Weekdays, weekends, dates unavailable, etc."
                                    required
                                    testId="casting-input-availability"
                                />

                                <TextAreaField
                                    label="Additional Notes (optional)"
                                    name="additional_notes"
                                    value={form.additional_notes}
                                    onChange={handleChange}
                                    placeholder="Anything else we should know."
                                    rows={3}
                                    testId="casting-input-notes"
                                />

                                <button
                                    type="submit"
                                    data-testid="casting-submit-btn"
                                    disabled={submitting}
                                    className="w-full inline-flex items-center justify-center gap-3 bg-blood text-white font-accent uppercase tracking-widest text-lg py-4 px-8 hover:bg-bloodHover transition-colors border border-blood disabled:opacity-50"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            Submit Application <ArrowRight size={16} />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

const Stat = ({ label, value }) => (
    <div className="border border-neutral-300 bg-ink p-4">
        <p className="font-mono text-sm uppercase tracking-widest text-neutral-500">
            {label}
        </p>
        <p className="font-display text-4xl text-neutral-900 mt-1">{value}</p>
    </div>
);

const FieldWrapper = ({ label, children }) => (
    <label className="block">
        <span className="font-mono text-base uppercase tracking-widest text-neutral-600 mb-2 block">
            {label}
        </span>
        {children}
    </label>
);

const Field = ({ label, testId, ...rest }) => (
    <FieldWrapper label={label}>
        <input
            data-testid={testId}
            {...rest}
            className="w-full bg-ink border-b border-neutral-400 focus:border-blood text-neutral-900 font-mono text-lg py-3 px-0 outline-none transition-colors placeholder:text-neutral-500"
        />
    </FieldWrapper>
);

const TextAreaField = ({ label, testId, ...rest }) => (
    <FieldWrapper label={label}>
        <textarea
            data-testid={testId}
            {...rest}
            className="w-full bg-ink border border-neutral-300 focus:border-blood text-neutral-900 font-mono text-lg py-3 px-4 outline-none transition-colors placeholder:text-neutral-500 resize-none"
        />
    </FieldWrapper>
);

const SelectField = ({ label, testId, options, ...rest }) => (
    <FieldWrapper label={label}>
        <select
            data-testid={testId}
            {...rest}
            className="w-full bg-ink border-b border-neutral-400 focus:border-blood text-neutral-900 font-mono text-lg py-3 px-0 outline-none transition-colors appearance-none cursor-pointer"
        >
            <option value="" disabled>
                Select a role…
            </option>
            {options.map((o) => (
                <option key={o} value={o} className="bg-ink2">
                    {o}
                </option>
            ))}
        </select>
    </FieldWrapper>
);
